import { selectCurrentHotelName } from 'accessors'
import { updateActiveServices } from 'actions/hotelDetails'
import CloseIcon from 'components/ReusableComponents/CloseIcon'
import Dialog from 'components/ReusableComponents/Dialog'
import AnnouncementsIcon from 'components/ReusableComponents/Icons/Announcement'
import ChatIcon from 'components/ReusableComponents/Icons/Chat'
import ChatBotIcon from 'components/ReusableComponents/Icons/ChatBot'
import InviteIcon from 'components/ReusableComponents/Icons/Invite'
import LockOpen from 'components/ReusableComponents/Icons/LockOpen'
import TextBlastIcon from 'components/ReusableComponents/Icons/TextBlast'
import { directfulServiceTypes } from 'main/directfulServices'
import moment from 'moment'
import { createContext, useContext, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'
import subsidiariesApi from 'services/api/subsidiaries'

import ConfirmationDialog from '../ConfirmationDialog'
import styles from './UnlockServiceDialog.module.scss'

const UnlockServiceContext = createContext()

export const services = {
  marketing: 'marketing',
  messaging: 'messaging',
  unlimited: 'unlimited'
}

export const features = {
  Messaging: 'Messaging',
  SalesMessaging: 'Sales messaging',
  Campaigns: 'Advanced campaigns',
  ChatBotResponses: 'ChatBot responses',
  Announcements: 'Announcements',
  TextBlast: 'Text blast',
  UnlimitedFeatures: 'Unlimited features'
}

function getServiceFromFeature(feature) {
  switch (feature) {
    case features.Messaging:
      return services.messaging
    case features.SalesMessaging:
      return services.marketing
    case features.Campaigns:
      return services.marketing
    case features.ChatBotResponses:
      return services.messaging
    case features.Announcements:
      return services.marketing
    case features.TextBlast:
      return services.marketing
    case features.UnlimitedFeatures:
      return services.unlimited
    default:
      return null
  }
}

export const UnlockServiceProvider = ({ children }) => {
  const { addToast } = useToasts()
  const [isDialogVisible, setIsDialogVisible] = useState(false)
  const [feature, setFeature] = useState(null)
  const [service, setService] = useState(null)
  const [isNoPermissionDialogVisible, setNoPermissionDialogVisible] = useState(false)

  function openDialog(feature) {
    setFeature(feature)
    setService(getServiceFromFeature(feature))
    setIsDialogVisible(true)
  }

  function closeDialog() {
    setIsDialogVisible(false)
  }

  function closeNoPermissionDialog() {
    setNoPermissionDialogVisible(false)
    closeDialog()
  }

  const dispatch = useDispatch()

  function confirmDialog(rate) {
    const newPlanData = {
      plan: 'Unlimited',
      rate: rate
    }

    const data = {
      plan: 'Unlimited',
      startDate: moment().format('YYYY-MM-DD'),
      isFlatMonthly: true
    }

    subsidiariesApi
      .upgradeToUnlimited(data)
      .then(res => {
        if (res) {
          addToast('Hotel upgraded to In-property unlimited successfully', {
            appearance: 'success'
          })
          dispatch(updateActiveServices([newPlanData]))
        }
      })
      .catch(err => {
        addToast(`${err}`, {
          appearance: 'error',
          autoDismiss: true
        })
      })
      .finally(() => closeDialog())
  }

  return (
    <UnlockServiceContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      {isDialogVisible && (
        <UnlockServiceDialog
          feature={feature}
          service={service}
          onClose={() => setIsDialogVisible(false)}
          onConfirm={confirmDialog}
          isApprovalDialogVisible={isNoPermissionDialogVisible}
          openPermissionDialog={() => setNoPermissionDialogVisible(true)}
          closePermissionDialog={closeNoPermissionDialog}
        />
      )}
    </UnlockServiceContext.Provider>
  )
}

const UnlockServiceDialog = ({
  onClose,
  feature,
  service,
  isApprovalDialogVisible,
  closePermissionDialog
}) => {
  const hotelName = useSelector(selectCurrentHotelName)

  const userName = useSelector(s => s.login.currentUser.name)

  function openChat() {
    window.$crisp.push(['do', 'chat:open'])
    window.$crisp.push([
      'set',
      'message:text',
      [`I want to unlock ${service} service for ${hotelName}`]
    ])
    onClose()
  }

  return (
    <>
      {isApprovalDialogVisible && (
        <ConfirmationDialog
          title="Insufficient Permissions"
          positiveMessage="Okay"
          onConfirm={() => closePermissionDialog()}
          hasCloseButton={false}
          theme="blacklist"
        >
          <div className={styles.approvalNeededMsg}>
            An admin or an user with billing access needs to perform this action.
          </div>
        </ConfirmationDialog>
      )}
      <Dialog>
        <div
          className={`${styles.dialog} ${
            feature === features.UnlimitedFeatures ? styles.largeContainer : ''
          }`}
        >
          <div className={styles.header}>
            <button>
              <CloseIcon onClick={onClose} className={styles.closeIcon} />
            </button>
          </div>
          <div className={styles.content}>
            {(feature === features.Messaging || feature === features.SalesMessaging) && (
              <div className={styles.iconHolder}>
                <ChatIcon />
              </div>
            )}
            {(feature === features.Campaigns || features.UnlimitedFeatures) && (
              <div className={styles.iconHolder}>
                <InviteIcon />
              </div>
            )}
            {feature === features.ChatBotResponses && (
              <div className={styles.iconHolder}>
                <ChatBotIcon />
              </div>
            )}
            {feature === features.Announcements && (
              <div className={styles.iconHolder}>
                <AnnouncementsIcon />
              </div>
            )}
            {feature === features.TextBlast && (
              <div className={styles.iconHolder}>
                <TextBlastIcon />
              </div>
            )}
            {/* <p className={styles.featureName}>{feature}</p> */}
            {feature === features.ChatBotResponses && (
              <>
                {/* <p className={styles.title}>
                Automatically respond to common guest questions
              </p> */}
                <p className={styles.mainText}>
                  Hello {userName}, we're excited to introduce our new In-Property
                  Messaging Package designed to enhance your guests' experience during
                  their stay at your hotel. With this package, you can provide superior
                  communication and personalized service at every touchpoint. However,
                  it's currently not included in your plan. To unlock these powerful tools
                  and create unforgettable guest experiences, simply click the 'Unlock'
                  button below. Your dedicated account manager will be there to guide you
                  through the seamless setup process. Thank you for being a valued partner
                  in delivering exceptional hospitality.
                </p>
              </>
            )}
            {feature === features.Messaging && (
              <>
                {/* <p className={styles.title}>Chat with your guests</p> */}
                <p className={styles.mainText}>
                  Hello {userName}, We're excited to introduce our new In-Property
                  Messaging Package designed to enhance your guests' experience during
                  their stay at your hotel. With this package, you can provide superior
                  communication and personalized service at every touchpoint. However,
                  it's currently not included in your plan. To unlock these powerful tools
                  and create unforgettable guest experiences, simply click the 'Unlock'
                  button below. Your dedicated account manager will be there to guide you
                  through the seamless setup process. Thank you for being a valued partner
                  in delivering exceptional hospitality.
                </p>
              </>
            )}
            {feature === features.SalesMessaging && (
              <>
                {/* <p className={styles.title}>Reach guests at the right time</p> */}
                <p className={styles.mainText}>
                  Hello {userName}, great news! Our marketing package is designed to boost
                  your revenue by leveraging powerful features tailored for your success.
                  However, it's currently not included in your plan. To unlock these
                  revenue-driving tools, simply click the 'Unlock' button below and your
                  dedicated account manager will guide you through the process. Thank you
                  for being a valued customer!
                </p>
              </>
            )}
            {feature === features.Campaigns && (
              <>
                {/* <p className={styles.title}>Reach guests at the right time</p> */}
                <p className={styles.mainText}>
                  Hello {userName}, great news! Our marketing package is designed to boost
                  your revenue by leveraging powerful features tailored for your success.
                  However, it's currently not included in your plan. To unlock these
                  revenue-driving tools, simply click the 'Unlock' button below and your
                  dedicated account manager will guide you through the process. Thank you
                  for being a valued customer!
                </p>
              </>
            )}
            {feature === features.Announcements && (
              <>
                {/* <p className={styles.title}>Send announcements to your guests</p> */}
                <p className={styles.mainText}>
                  Hello {userName}, great news! Our marketing package is designed to boost
                  your revenue by leveraging powerful features tailored for your success.
                  However, it's currently not included in your plan. To unlock these
                  revenue-driving tools, simply click the 'Unlock' button below and your
                  dedicated account manager will guide you through the process. Thank you
                  for being a valued customer!
                </p>
              </>
            )}
            {feature === features.TextBlast && (
              <>
                {/* <p className={styles.title}>Send text messages to your guests</p> */}
                <p className={styles.mainText}>
                  Hello {userName}, great news! Our marketing package is designed to boost
                  your revenue by leveraging powerful features tailored for your success.
                  However, it's currently not included in your plan. To unlock these
                  revenue-driving tools, simply click the 'Unlock' button below and your
                  dedicated account manager will guide you through the process. Thank you
                  for being a valued customer!
                </p>
              </>
            )}
            {feature === features.UnlimitedFeatures && (
              <>
                <p className={styles.subtitle}>Directful Unlimited</p>
                <p className={styles.title}>Unlock Unlimited Messaging Today!</p>
                <p className={styles.mainText}>
                  Elevate your guest engagement by enabling "Directful Unlimited"
                  messaging. Share key announcements, drive attendance to events like
                  happy hours, and offer guests exclusive deals to extend their stay with
                  personalized communication. Whether it's an in-house event, today's
                  arrivals, or enticing tomorrow's departures with a possible % discount
                  on an extra night, this powerful tool helps you fill rooms and enhance
                  guest satisfaction. Don't miss out on the opportunity to boost your
                  guest interaction and revenue—enable it now!
                </p>
              </>
            )}
            <button className={styles.unlockButton} onClick={() => openChat()}>
              <LockOpen />
              <span className={styles.buttonText}>
                {feature === features.UnlimitedFeatures
                  ? 'Unlock Directful Unlimited'
                  : `Unlock ${service} service`}
              </span>
            </button>
          </div>
        </div>
      </Dialog>
    </>
  )
}

export const useUnlockService = () => {
  const services = useSelector(s => s.login.activeServices)
  const activeServices = useSelector(s => s.hotelDetails.activeServices)
  const isPaused = useSelector(s => s.hotelDetails.status === 'Paused')

  const { openDialog: openUnlockServiceDialog, closeDialog: closeUnlockServiceDialog } =
    useContext(UnlockServiceContext)

  const hasMarketingService = true

  const hasMessagingService = true
  const hasUnlimitedService = activeServices.find(
    s => s.plan === directfulServiceTypes.Unlimited
  )
  const hasBasicService = activeServices.find(s => s.plan === directfulServiceTypes.Basic)

  return {
    openUnlockServiceDialog,
    closeUnlockServiceDialog,
    services,
    hasMarketingService,
    hasMessagingService,
    isPaused,
    hasUnlimitedService,
    hasBasicService
  }
}

export default UnlockServiceDialog
